import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  Sparkles,
  ArrowRight,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Star,
} from 'lucide-angular';
import { AuthService } from '../../../../common/auth/auth.service';

@Component({
  selector: 'app-step-auth',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink],
  templateUrl: './step-auth.html',
  styleUrl: './step-auth.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepAuthV2Component {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly errorMessage$ = new BehaviorSubject<string>('');
  protected readonly isUserExistsError$ = new BehaviorSubject<boolean>(false);
  protected readonly showPassword = signal(false);

  protected readonly authForm: FormGroup;

  protected readonly icons = {
    Sparkles,
    ArrowRight,
    Eye,
    EyeOff,
    Mail,
    Lock,
    Star,
  };

  protected readonly nextStepUrl = '/wizard/v2/profile';

  constructor() {
    this.authForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected onSubmit(): void {
    if (this.authForm.invalid) {
      this.authForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);
    this.errorMessage$.next('');

    const { email, password } = this.authForm.value;
    const request = { email, password };

    this.authService
      .signUp(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading$.next(false);
          this.router.navigate([this.nextStepUrl]);
        },
        error: (error) => {
          this.isLoading$.next(false);
          const isConflict = error.status === 409;
          this.isUserExistsError$.next(isConflict);
          if (isConflict) {
            this.errorMessage$.next('An account with this email already exists.');
          } else {
            this.errorMessage$.next(error.error?.message || 'An error occurred. Please try again.');
          }
        },
      });
  }

  protected signInWithGoogle(): void {
    this.authService.signInWithGoogle(this.nextStepUrl);
  }

  protected signInWithMicrosoft(): void {
    this.authService.signInWithMicrosoft(this.nextStepUrl);
  }
}

